import React, { useMemo, useState } from "react";
import Icon from "../Icon";
import MockFormModal from "./MockFormModal";
import { matchesMockSearch } from "./mockData";

const DEFAULT_COLOR = "#6366f1";
const EMPTY_FORM = { name: "", color: DEFAULT_COLOR };

const MockDepartments = ({
    departments,
    onAddDepartment,
    onUpdateDepartment,
    onDeleteDepartment,
    showConfirm,
    searchQuery = "",
}) => {
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [error, setError] = useState("");

    const filteredDepartments = useMemo(
        () => departments.filter((d) =>
            matchesMockSearch(searchQuery, d.name, d.users, d.emails, d.tickets)
        ),
        [departments, searchQuery]
    );

    const resetForm = () => {
        setForm(EMPTY_FORM);
        setError("");
        setEditingId(null);
        setShowModal(false);
    };

    const openAddModal = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setError("");
        setShowModal(true);
    };

    const openEditModal = (department) => {
        setEditingId(department.id);
        setForm({
            name: department.name,
            color: department.color,
        });
        setError("");
        setShowModal(true);
    };

    const handleSave = () => {
        const trimmed = form.name.trim();
        if (!trimmed) {
            setError("Enter a department name.");
            return;
        }
        const duplicate = departments.some(
            (d) => d.name.toLowerCase() === trimmed.toLowerCase() && d.id !== editingId
        );
        if (duplicate) {
            setError("A department with this name already exists.");
            return;
        }

        const payload = { name: trimmed, color: form.color };

        if (editingId) {
            onUpdateDepartment(editingId, payload);
        } else {
            onAddDepartment(payload);
        }
        resetForm();
    };

    const handleDelete = (department) => {
        if (departments.length <= 1) {
            return;
        }
        showConfirm?.({
            title: "Are you sure you want to remove this department?",
            message: "Linked tickets will be marked Unassigned. Incoming emails will be moved to another department.",
            onConfirm: () => {
                onDeleteDepartment(department.id);
                if (editingId === department.id) resetForm();
            },
        });
    };

    return (
        <div className="clk-mk-page clk-mk-page--departments">
            <div className="clk-mk-page-header-row">
                <h5>Departments</h5>
                <button type="button" className="clk-mk-primary-btn" onClick={openAddModal}>
                    <img src="/images/plus.svg" alt="" aria-hidden="true" />
                    Add Department
                </button>
            </div>

            {showModal && (
                <MockFormModal
                    title={editingId ? "Edit department" : "New department"}
                    onClose={resetForm}
                >
                    <label className="clk-mk-field">
                        <span>Name</span>
                        <input
                            type="text"
                            value={form.name}
                            placeholder="e.g. Product Support"
                            onChange={(e) => {
                                const { value } = e.target;
                                setForm((f) => ({ ...f, name: value }));
                                setError("");
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleSave();
                            }}
                        />
                    </label>
                    <label className="clk-mk-field clk-mk-field--color">
                        <span>Color</span>
                        <div className="clk-mk-color-row">
                            <input
                                type="color"
                                value={form.color}
                                onChange={(e) => {
                                    const { value } = e.target;
                                    setForm((f) => ({ ...f, color: value }));
                                }}
                                aria-label="Department color"
                            />
                            <span className="clk-mk-dept">
                                <i style={{ background: form.color }} />
                                {form.name.trim() || "Preview"}
                            </span>
                        </div>
                    </label>
                    {error && <p className="clk-mk-form-error">{error}</p>}
                    <div className="clk-mk-create-actions">
                        <button type="button" className="clk-mk-link-btn" onClick={resetForm}>
                            Cancel
                        </button>
                        <button type="button" className="clk-mk-primary-btn" onClick={handleSave}>
                            {editingId ? "Save changes" : "Create department"}
                        </button>
                    </div>
                </MockFormModal>
            )}

            {filteredDepartments.length === 0 ? (
                <p className="clk-mk-empty">No departments match your search.</p>
            ) : (
                <div className="clk-mk-table-card">
                    <table className="clk-mk-app-table">
                        <thead>
                            <tr>
                                <th>Department</th>
                                <th>Color</th>
                                <th>Users</th>
                                <th>Emails</th>
                                <th>Tickets</th>
                                <th className="clk-mk-col-actions" aria-label="Actions">
                                    <span className="clk-mk-sr-only">Actions</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDepartments.map((d) => (
                                <tr key={d.id}>
                                    <td>{d.name}</td>
                                    <td>
                                        <span className="clk-mk-dept-badge">
                                            <i style={{ background: d.color }} />
                                            {d.name}
                                        </span>
                                    </td>
                                    <td>{d.users}</td>
                                    <td>{d.emails}</td>
                                    <td>{d.tickets}</td>
                                    <td className="clk-mk-col-actions">
                                        <div className="clk-mk-row-actions">
                                            <button
                                                type="button"
                                                className="clk-mk-icon-btn"
                                                aria-label={`Edit ${d.name}`}
                                                onClick={() => openEditModal(d)}
                                            >
                                                <Icon name="pencil" size={13} />
                                            </button>
                                            <button
                                                type="button"
                                                className="clk-mk-icon-btn clk-mk-icon-btn--danger"
                                                aria-label={`Delete ${d.name}`}
                                                onClick={() => handleDelete(d)}
                                                disabled={departments.length <= 1}
                                                title={departments.length <= 1 ? "At least one department is required" : undefined}
                                            >
                                                <Icon name="trash" size={13} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default MockDepartments;
